import { defineStore } from 'pinia'
import { randomString } from '@/composables/helperFunctions.js'
import { useBranchString, useVersionString } from '@/composables/version.js'
import { useInstanceStore } from '@/store/backendInstances.js'
import { useDownloadFile } from '@/composables/downloadFile.js'
import { parse, stringify } from 'zipson'

interface LogsState {
  sessions: Record<string, LogSession>
  logSession: string | null
}

type LogSession = {
  entries: LogEntry[]
  started?: number
  lastUpdated?: number
}

export type AddLogEntry = {
  message: string
  level: LogLevel
  tag?: Tag
  globalUniqueIdentifier?: string
  error?: Error | string
  httpMethod?: string
  timestamp?: number
}

export type LogEntry = Omit<AddLogEntry, 'timestamp'> & {
  version: string
  branch: string
  session: string
  timestamp: number
}

type LogLevel = 'INFO' | 'WARN' | 'ERROR'

type Tag =
  | 'artifact'
  | 'backendAPI'
  | 'backendHandler'
  | 'backendInstances'
  | 'backendRequest'
  | 'canvas'
  | 'canvasElementProvider'
  | 'cameras'
  | 'chartCreator'
  | 'dataCollector'
  | 'displayModeSchema'
  | 'displayModeStore'
  | 'frameAnnotation'
  | 'instance'
  | 'logs'
  | 'main'
  | 'pullToRefresh'
  | 'render'
  | 'router'
  | 'syncable'
  | 'syncableHandler'

let currentBatch: LogEntry[] = []
let interval: number | null = null

export const useLogsStore = defineStore('logs', {
  state: (): LogsState => ({
    sessions: {},
    logSession: null,
  }),
  persist: {
    pick: ['sessions'],
    serializer: {
      deserialize: parse,
      serialize: stringify,
    },
  },
  getters: {
    getSessions: state => state.sessions,
    getLogEntries: state => Object.values(state.sessions).flatMap(session => session.entries),
    getLogEntriesForCurrentSession: state => (state.logSession && state.sessions[state.logSession] ? state.sessions[state.logSession].entries : []),
  },
  actions: {
    addLogEntry({
      message,
      tag,
      globalUniqueIdentifier = undefined,
      level = 'INFO',
      error = undefined,
      httpMethod = undefined,
      timestamp = Date.now(),
    }: AddLogEntry) {
      if (!this.logSession) this.logSession = randomString(8)
      if (globalUniqueIdentifier === undefined) globalUniqueIdentifier = useInstanceStore().selectedInstanceId

      if (level === 'ERROR' && ['dev', 'staging', 'local'].includes(useBranchString()))
        // eslint-disable-next-line no-console
        console.error(message, error)

      if (level === 'WARN' && ['dev', 'staging', 'local'].includes(useBranchString()))
        // eslint-disable-next-line no-console
        console.warn(message, error)

      const errorMessage = error instanceof Error ? error.message : error
      const entry = {
        version: useVersionString(),
        branch: useBranchString(),
        globalUniqueIdentifier: globalUniqueIdentifier!,
        session: this.logSession,
        message,
        error: errorMessage,
        tag,
        level,
        timestamp,
        httpMethod,
      }

      currentBatch.push(entry)
      this.ensureInterval()
    },
    ensureInterval() {
      if (interval) return
      interval = setInterval(() => {
        if (!currentBatch || currentBatch.length === 0 || !this.logSession) return
        const firstEntry = currentBatch[0]
        const lastEntry = currentBatch.at(-1)!
        const sessionExists = this.sessions[this.logSession]
        if (sessionExists) {
          this.sessions[this.logSession] = {
            entries: this.sessions[this.logSession].entries.concat(currentBatch),
            started: this.sessions[this.logSession].started,
            lastUpdated: lastEntry.timestamp,
          }
        } else {
          this.sessions[this.logSession] = {
            entries: currentBatch,
            started: firstEntry.timestamp,
            lastUpdated: lastEntry.timestamp,
          }
        }
        currentBatch = []
        let deletedOldSession = true
        while (deletedOldSession) {
          deletedOldSession = this.clearOldLogs()
        }
      }, 10000)
    },
    clearOldLogs() {
      if (Object.keys(this.sessions).length <= 5) return false
      let oldestTimestamp = Date.now()
      let oldestSession
      Object.keys(this.sessions).forEach(sessionId => {
        const session = this.sessions[sessionId]
        if (session?.lastUpdated && session.lastUpdated <= oldestTimestamp) {
          oldestTimestamp = session.lastUpdated
          oldestSession = sessionId
        }
      })
      if (oldestSession) {
        delete this.sessions[oldestSession]
        return true
      }
      return false
    },
    clearAllLogs() {
      this.sessions = {}
    },
    downloadLogs() {
      let text = ''
      this.getLogEntries.forEach(entry => {
        text +=
          new Date(entry.timestamp).toISOString() +
          ' ' +
          entry.level +
          ' ' +
          entry.tag +
          ' ' +
          entry.globalUniqueIdentifier +
          ' ' +
          entry.httpMethod +
          ' ' +
          entry.message +
          '\n'
      })
      const blob = new Blob([text], { type: 'text/plan;charset=utf-8' })
      useDownloadFile(blob, new Date().toISOString() + '_logs.txt')
    },
    async importLogs(file: File): Promise<AddLogEntry[]> {
      const newEntries: AddLogEntry[] = []

      try {
        const text = await file.text()
        const lines = text.split('\n').filter(line => line.trim() !== '')

        lines.forEach(line => {
          const parts = line.split(' ')

          if (parts.length < 6) {
            this.addLogEntry({
              message: `Invalid log line skipped during upload: ${line}`,
              level: 'WARN',
              tag: 'logs',
            })
            return
          }

          const timestampStr = parts[0]
          const level = parts[1] as LogLevel
          const tag = parts[2] as Tag
          const globalUniqueIdentifier = parts[3] === 'undefined' ? undefined : parts[3]
          const httpMethod = parts[4] === 'undefined' ? undefined : parts[4]
          const message = parts.slice(5).join(' ')

          const timestamp = new Date(timestampStr).getTime()

          if (Number.isNaN(timestamp)) {
            this.addLogEntry({
              message: `Invalid timestamp in line: ${line}`,
              level: 'WARN',
              tag: 'logs',
            })
            return
          }

          const entry: AddLogEntry = {
            timestamp,
            level,
            tag,
            globalUniqueIdentifier,
            httpMethod,
            message,
            error: undefined,
          }

          newEntries.push(entry)
        })

        return newEntries
      } catch (e) {
        this.addLogEntry({
          message: 'Error uploading logs: ' + (e instanceof Error ? e.message : String(e)),
          level: 'ERROR',
          tag: 'logs',
          error: e instanceof Error ? e.stack : String(e),
        })

        return []
      }
    },
  },
})
